import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import {
  type CandidateParent,
  type CounterpartyKind,
  type SelectableParty,
  type UploadNameType,
  type UploadOptionsResponse,
  type UploadRelationship,
  type UploadResponse,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import {
  fetchUploadOptions,
  refusalFieldError,
  sha256OfFile,
  submitUpload,
} from '../api/upload';
import { loginRequest, msalInstance } from '../auth/msal';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { ErrorNotice } from '../components/ErrorNotice';
import { PartyPicker } from '../components/PartyPicker';
import { TagInput } from '../components/TagInput';
import { VendorPicker } from '../components/VendorPicker';
import { requireSurface } from '../routes/surfaces';
import { SurfaceInfo } from '../shell/SurfaceInfo';
import { BaseDeviationOffer } from './BaseDeviationOffer';
import { CONTRACT_TYPE_SOURCE_COPY } from './type-proposal-copy';
import { UploadBundleMode } from './UploadBundleReview';

/**
 * Add a contract — CLM-30. FR-13 (controlled manual upload), FR-14 (no
 * free-text keys), IR-05 (the document stays in SharePoint).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ## There is no contract-key input on this page, and that is the feature
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * FR-14 exists because hand-typed identifiers are the documented failure mode.
 * The requirement is met by *absence*: `UploadRequestSchema` has no
 * `contract_key` field, so there is nothing for a form control to bind to, and
 * `AddContract.test.tsx` asserts that no such input exists in the DOM. The key
 * is minted server-side from the party and the sequence number the server
 * offered — `PartyKey-nnnn`, with no type (D-110-1) — and this page
 * **displays** the projection as soon as a party is chosen, so a user can see
 * what they are about to create.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ## Nobody is asked for a type (CLM-118, D-110-3)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * There is no type control on this page and no type in the request (B9). The
 * type comes from the **file name** first — a type word (`MSA`, `NDA`, `SOW`) or
 * a SOW-shaped number — and from the **AI's reading** second. The selected
 * file's own name travels to `upload-options` as `client_file_name`, and the
 * server answers with what the deterministic rule reads from it (`name_type`),
 * so the form says before upload what the type will be:
 *
 *  - a **name line** under the file: "From the file name: MSA", "The file name
 *    gives no type — the AI reads the document after upload", or "The file name
 *    names more than one type — the AI decides after upload"; and, when the name
 *    says Amendment, Addendum or the like, "Looks like an amendment";
 *  - the **SOW number**: required, and pre-filled from the name, when the name
 *    says SOW; not applicable when it says MSA or NDA or names more than one
 *    type (a number there is `400 sow_number_conflicts_with_name`); optional when
 *    it gives no type ("giving one files it as a SOW"); hidden for `amends`,
 *    which takes its agreement's number;
 *  - `amends` and `supersedes` take the related contract's type ("Follows
 *    SOW"), and `amends` is offered only against a contract whose primary party
 *    is the chosen client (`party_is_primary`): an amendment's key, file name
 *    and primary party must all name its agreement's client;
 *  - the success panel shows the **resolved** type through the tag — Type
 *    pending, provisional, or settled — where it came from, and what the file
 *    name gave; the CLM-112 offer reads that resolved type.
 *
 * When the file name and the AI's reading agree, the type is confirmed with no
 * administrator step; otherwise an administrator decides it.
 *
 * Everything selectable here is offered by `upload-options`:
 *
 *  - **parties** the caller's scope admits, so an out-of-scope client is not
 *    "rejected on submit" — it is not in the list;
 *  - the **file name's type** (`name_type`), read by the same rule the server
 *    files with — never a list of types to pick from;
 *  - **parent candidates**, with the §8.1 relationships each is valid for
 *    computed server-side. Rule 1 (*"an amendment may only target an agreement
 *    that is signed and not ended"*, D-124-1) is a cross-row check, so offering
 *    only valid targets is the difference between a disabled option and a
 *    rejected submission.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ## §4C: the warning arrives before the file does
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `processing_restricted` is on each selectable party, so choosing a restricted
 * client shows the notice **before** a document is picked. A user who learns
 * after uploading that the facts must be entered by hand has done the work
 * twice. The notice is a policy state, not an error, and is styled as one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ## Rule 11, and the one thing this page computes
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Everything goes through `src/api/*` — `apps/web/eslint.config.js` bans `fetch`
 * outside that directory. The single computation here is the file's SHA-256,
 * which the descriptor carries so a **truncated** upload is refused rather than
 * filed as a valid but incomplete contract. The server recomputes it and uses
 * its own value; the client's is a corruption check, never an authorisation one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ## Accessibility (the CLM-51 bar)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every control has a real `<label for>`. The outcome region is
 * `aria-live="polite"` **and** focusable, and focus moves to it on submit — a
 * live region alone announces to a screen reader and leaves a sighted keyboard
 * user's focus on a button whose page has changed underneath it. `SurfaceInfo`
 * carries the surface's own summary through the same `role="tooltip"` +
 * `aria-describedby` pattern the nav rail uses, so there is one implementation
 * of that affordance and not two.
 */

/* -------------------------------------------------------------------------- */
/* State                                                                       */
/* -------------------------------------------------------------------------- */

type OptionsPhase =
  | { readonly kind: 'loading' }
  /**
   * `for_file_name` — the file name the options were read for (CLM-118): their
   * `name_type` describes that file only, so a newer file waits for its own read.
   */
  | { readonly kind: 'ready'; readonly options: UploadOptionsResponse; readonly for_file_name: string | null }
  /**
   * CLM-57 Part 1. `GET /v1/contracts/upload-options` failed for a reason other
   * than 401, so filing is unavailable — but the action is **still rendered**,
   * disabled, with a sentence saying which call failed, how it was classified
   * and what to do. `problem` is `classifyApiError`'s kind: `unknown` for a
   * network failure (prod today, B-33 — the API has never been deployed),
   * `not_implemented` for `501`, `forbidden` for `403`.
   */
  | {
      readonly kind: 'unavailable';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

/** CLM-57 — one document through the guided form, or several through plan → review → execute. */
type Mode = 'single' | 'bundle';

type SubmitPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'hashing' }
  | { readonly kind: 'submitting' }
  /**
   * The response carries the **resolved** type (CLM-110, R-110-16) but not the
   * relationship, so the relationship, its target and whether a SOW number was
   * given are kept from the request as submitted — the success copy and the
   * CLM-112 offer read them, never the form's current state, which the user
   * may already have changed.
   */
  | {
      readonly kind: 'accepted';
      readonly response: UploadResponse;
      readonly relationship: UploadRelationship['relationship'];
      readonly target_contract_key: string | null;
      readonly sow_number_given: boolean;
    }
  | {
      readonly kind: 'refused';
      readonly message: string;
      readonly correlation_id: string | null;
      readonly status: number | null;
      /**
       * The gateway's one permitted body variation, when it sent one — the
       * `field_errors[0]` entry `refusalFieldError` parses out. It is what lets a
       * `409` say *"already filed as SGW-SOW-0004"* rather than *"something
       * conflicts"*: the `path` is the cause and the `message` names the row.
       */
      readonly field_error: { readonly path: string; readonly message: string } | null;
    };

type RelationshipKind = UploadRelationship['relationship'];

const RELATIONSHIP_LABEL: Readonly<Record<RelationshipKind, string>> = {
  standalone: 'Stands alone',
  child_of: 'Sits under (a SOW under its MSA)',
  amends: 'Amends (the agreement keeps its status)',
  supersedes: 'Supersedes (the target becomes read-only)',
};

/**
 * How the SOW number field behaves (R-110-16 as CLM-118 amends it): hidden for an
 * amendment (it takes its agreement's number) and for a `supersedes` of a
 * non-SOW; required for a `supersedes` of a SOW and when the file name says SOW;
 * not applicable when the name says MSA or NDA or names more than one type (the
 * server refuses a number there, `sow_number_conflicts_with_name`); optional
 * otherwise — with no type in the name, giving one files the contract as a SOW.
 */
type SowField = 'hidden' | 'required' | 'optional' | 'not_applicable';

/**
 * The advice a refusal reason gets beside the gateway's own sentence. Each says
 * something different to act on (`UPLOAD_CONFLICT_REASONS`): only a stale
 * sequence is cured by reloading, because a `key_collision` would be offered
 * the same number again (R-110-10). CLM-118 adds the `400` that a SOW number
 * beside a file name that says another type earns.
 */
const REFUSAL_ADVICE: Readonly<Record<string, string>> = {
  sequence_number_stale:
    'Somebody filed for this client while you were on this page. Reload the form to pick up the next free number.',
  key_collision:
    'The ID the API minted for this client is already held — by a contract outside your scope, or one filed ' +
    'without the usual lock. Nothing was filed, and trying again would be offered the same number: ask an ' +
    'administrator who can see all of this client’s contracts to file it.',
  /* CLM-118 (B8) — no actor named: an Admin, or the file name and the AI agreeing, may have settled it. */
  agreement_type_changed:
    'The type of the contract this one relates to was settled or changed while you were on this page, so ' +
    'nothing was filed. Choose the related contract again to see its type now, then file.',
  sow_number_conflicts_with_name:
    'The file name says MSA or NDA, or names more than one type, so it cannot also carry a SOW number. If this is a ' +
    'SOW, rename the file so its name says SOW or carries its SOW number, and file it again.',
};

/** The `reason:` prefix of the gateway's field error — `null` when there is none. */
function refusalReason(fieldError: { readonly message: string } | null): string | null {
  if (fieldError === null) return null;
  const reason = fieldError.message.split(':', 1)[0]?.trim() ?? '';
  return reason === '' ? null : reason;
}

/**
 * Where the resolved type came from, in the uploader's words — read from what the
 * file name gave (`contract_type_name_evidence`, CLM-118), never from the type
 * alone: a Type pending `OTHER` and a real one are different facts.
 */
function resolvedTypeWords(phase: Extract<SubmitPhase, { kind: 'accepted' }>): string {
  const { response } = phase;
  if (response.contract_type_source === 'inherited') {
    return phase.target_contract_key === null ? CONTRACT_TYPE_SOURCE_COPY.inherited : `follows ${phase.target_contract_key}`;
  }
  if (phase.relationship === 'supersedes' && phase.target_contract_key !== null) return `follows ${phase.target_contract_key}`;
  switch (response.contract_type_name_evidence) {
    case 'typed':
      return 'from the file name';
    case 'conflicting':
      return 'the file name names more than one type';
    case 'untyped':
      return response.contract_type === 'SOW' && response.contract_type_confirmation !== 'pending'
        ? 'from the SOW number'
        : 'the file name gave no type';
  }
}

/**
 * A related contract's type as the target select's words carry it (R-118-12, as
 * the server's candidate labels do): Type pending, provisional, or the type.
 */
function followsWords(target: Pick<CandidateParent, 'contract_type' | 'contract_type_confirmation'>): string {
  switch (target.contract_type_confirmation) {
    case 'pending':
      return 'Type pending';
    case 'provisional':
      return `${target.contract_type} (provisional)`;
    case 'confirmed':
    case 'auto_confirmed':
      return target.contract_type;
  }
}

/** CLM-118 — the name line under the file: what the file name gives, in the uploader's words. */
function nameLineWords(nameType: UploadNameType): string {
  switch (nameType.evidence) {
    case 'typed':
      return `From the file name: ${nameType.contract_type ?? ''}`;
    case 'untyped':
      return 'The file name gives no type — the AI reads the document after upload';
    case 'conflicting':
      return 'The file name names more than one type — the AI decides after upload';
  }
}

/* -------------------------------------------------------------------------- */
/* Surface                                                                     */
/* -------------------------------------------------------------------------- */

export function AddContract() {
  const surface = requireSurface('upload');

  const [partyKey, setPartyKey] = useState('');
  /** The party object the picker handed back — the §4C flag without a second lookup (CLM-68). */
  const [pickedParty, setPickedParty] = useState<SelectableParty | null>(null);
  const [relationship, setRelationship] = useState<RelationshipKind>('standalone');
  const [targetKey, setTargetKey] = useState('');
  const [sowNumber, setSowNumber] = useState('');
  /** CLM-77 — optional tags, stored in the same transaction as the row. */
  const [tags, setTags] = useState<readonly string[]>([]);
  /**
   * CLM-78 (R-76-2) — Client or Vendor. Request-time only, never stored (K5):
   * it swaps the picker and, for a vendor, travels as `counterparty_kind` so the API can refuse
   * a party whose `party_type` disagrees (400 `counterparty_kind_mismatch`).
   */
  const [counterpartyKind, setCounterpartyKind] = useState<CounterpartyKind>('client');
  const [file, setFile] = useState<File | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [options, setOptions] = useState<OptionsPhase>({ kind: 'loading' });
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>({ kind: 'idle' });
  const [mode, setMode] = useState<Mode>('single');
  /** Bumped by Retry: the options effect depends on it, so it re-runs `loadOptions`. */
  const [attempt, setAttempt] = useState(0);

  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** The SOW number the last file name pre-filled, so a newer name replaces it but never a number the user typed. */
  const prefilledSowRef = useRef<string | null>(null);
  const fileName = file?.name ?? null;

  /**
   * Re-fetched when the party or the file changes (CLM-118): the file's own name
   * gives `name_type`, and the type it gives narrows each parent's relationships
   * (B16). A name longer than the wire allows is not sent, and the type is then
   * the server's to read at upload.
   */
  const loadOptions = useCallback(
    async (party: string, clientFileName: string | null, controller: AbortController) => {
      try {
        const sendName = clientFileName !== null && clientFileName.length >= 1 && clientFileName.length <= 255;
        const next = await fetchUploadOptions(
          {
            ...(party === '' ? {} : { primary_party_key: party }),
            ...(sendName ? { client_file_name: clientFileName } : {}),
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setOptions({ kind: 'ready', options: next, for_file_name: clientFileName });
        /* D-71-0 — a name that says SOW pre-fills its number; a number the user typed is never replaced. */
        const candidate =
          next.name_type?.contract_type === 'SOW' ? next.name_type.sow_number_candidate : null;
        setSowNumber((typed) => {
          if (typed !== '' && typed !== prefilledSowRef.current) return typed;
          prefilledSowRef.current = candidate;
          return candidate ?? '';
        });
      } catch (error) {
        if (controller.signal.aborted) return;

        if (error instanceof ApiError && error.isUnauthenticated) {
          // Re-authenticate the way the rest of the app does: a redirect, never
          // a popup.
          void msalInstance.loginRedirect(loginRequest);
          return;
        }
        setOptions({
          kind: 'unavailable',
          problem: classifyApiError(error),
          /* The gateway's own message when there was a gateway; a network
           * failure's `TypeError: Failed to fetch` is not copy anyone should read. */
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    setOptions((current) => (current.kind === 'unavailable' ? { kind: 'loading' } : current));
    void loadOptions(partyKey, fileName, controller);
    return () => controller.abort();
  }, [partyKey, fileName, loadOptions, attempt]);

  const ready = options.kind === 'ready' ? options.options : null;
  const parties: readonly SelectableParty[] = ready?.parties ?? [];
  /* The picker's own last selection first: a party found by search need not be
   * in `ready.parties` (the first page), and the §4C notice must still render. */
  const selectedParty =
    pickedParty?.party_key === partyKey
      ? pickedParty
      : (parties.find((party) => party.party_key === partyKey) ?? null);
  /*
   * CLM-118 — what the chosen file's name gives: `undefined` while the options for this file are still
   * being read (the form waits), `null` with no file chosen.
   */
  const nameType: UploadNameType | null | undefined =
    file === null
      ? null
      : options.kind === 'ready' && options.for_file_name === fileName
        ? options.options.name_type
        : undefined;

  /**
   * Parents valid for the relationship the user has chosen. `amends` only against
   * a contract whose primary party is the chosen client (R-110-16,
   * `party_follows_agreement`) — the server withholds it too; this is the same
   * rule, so the list never offers what the filing would refuse.
   */
  const candidates: readonly CandidateParent[] = (ready?.candidate_parents ?? []).filter(
    (candidate) =>
      relationship === 'standalone' ||
      ((candidate.valid_relationships as readonly string[]).includes(relationship) &&
        (relationship !== 'amends' || candidate.party_is_primary)),
  );

  const needsTarget = relationship !== 'standalone';
  /** `amends` and `supersedes` take the target's type (§3.5 rule 1): the file name's type does not apply. */
  const followsTarget = relationship === 'amends' || relationship === 'supersedes';
  const target = candidates.find((candidate) => candidate.contract_key === targetKey) ?? null;
  /*
   * CLM-118 (B16) — a new file's name narrows each candidate's relationships, as the retired type select
   * did, and that select reset the choice. Once the options for the chosen file are read, a contract no
   * longer offered is cleared, so the select and what Submit sends agree.
   */
  const targetDropped = targetKey !== '' && ready !== null && nameType !== undefined && target === null;
  useEffect(() => {
    if (targetDropped) setTargetKey('');
  }, [targetDropped]);
  /*
   * "Follows SOW (provisional)" — the type the new contract takes, and whether it is settled (R-110-23,
   * R-118-12: an unsettled type never reads as settled, and Type pending never reads as OTHER). An
   * amendment takes the confirmation too. The words are the candidate label's.
   */
  const followsLabel = target === null ? 'the related contract' : followsWords(target);
  const sowField: SowField =
    relationship === 'amends'
      ? 'hidden'
      : relationship === 'supersedes'
        ? target?.contract_type === 'SOW'
          ? 'required'
          : 'hidden'
        : nameType?.contract_type === 'SOW'
          ? 'required'
          : nameType?.evidence === 'typed' || nameType?.evidence === 'conflicting'
            ? 'not_applicable'
            : 'optional';
  const busy = submitPhase.kind === 'hashing' || submitPhase.kind === 'submitting';

  /**
   * Submit is unavailable until the required choices are all present — for a SOW, the number too — and
   * the options have been read for the chosen file, so the SOW-number rule shown is the file's. No type
   * is asked for.
   */
  const canSubmit =
    !busy &&
    ready !== null &&
    partyKey !== '' &&
    file !== null &&
    nameType !== undefined &&
    ready.next_sequence_number !== null &&
    /* The chosen contract must still be offered: a stale key the candidates dropped is no target. */
    (!needsTarget || target !== null) &&
    (sowField !== 'required' || sowNumber.trim() !== '');

  async function run(document: File, controller: AbortController): Promise<void> {
    if (ready === null || ready.next_sequence_number === null) return;
    const sowSent = (sowField === 'required' || sowField === 'optional') && sowNumber.trim() !== '';

    try {
      setSubmitPhase({ kind: 'hashing' });
      const sha256 = await sha256OfFile(document);
      if (controller.signal.aborted) return;

      setSubmitPhase({ kind: 'submitting' });
      const response = await submitUpload(
        {
          primary_party_key: partyKey,
          /* CLM-118 — no type: the server reads it from `document.file_name`, the target, or a SOW number (B9). */
          sequence_number: ready.next_sequence_number,
          /**
           * A discriminated union, so the shape is built per branch rather than
           * assembled from nullable keys: `UploadRelationshipSchema` is a
           * `discriminatedUnion` precisely because a nullable trio of keys would
           * let `amends` and `supersedes` be set at once, which
           * `ck_contract_amend_xor_supersede` forbids.
           */
          relationship:
            relationship === 'standalone'
              ? { relationship: 'standalone' }
              : { relationship, target_contract_key: targetKey },
          document: {
            file_name: document.name,
            media_type: 'application/pdf',
            size_bytes: document.size,
            sha256,
          },
          ...(sowSent ? { sow_number: sowNumber.trim() } : {}),
          ...(tags.length > 0 ? { tags: [...tags] } : {}),
          /* CLM-78 — sent for a vendor agreement only, so a client upload is byte-identical
           * to CLM-76; the API refuses a party whose type disagrees (400 counterparty_kind_mismatch). */
          ...(counterpartyKind === 'vendor' ? { counterparty_kind: 'vendor' as const } : {}),
        },
        document,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setSubmitPhase({
        kind: 'accepted',
        response,
        relationship,
        target_contract_key: relationship === 'standalone' ? null : targetKey,
        sow_number_given: sowSent,
      });
    } catch (error) {
      if (controller.signal.aborted) return;

      if (error instanceof ApiError && error.isUnauthenticated) {
        void msalInstance.loginRedirect(loginRequest);
        return;
      }
      if (error instanceof ApiError) {
        setSubmitPhase({
          kind: 'refused',
          message: error.message,
          correlation_id: error.correlation_id,
          status: error.status,
          field_error: refusalFieldError(error.body),
        });
        return;
      }
      const offContract = error instanceof Error && error.name === 'ZodError';
      setSubmitPhase({
        kind: 'refused',
        message: offContract
          ? 'The API answered, but its response did not match the upload contract this build was compiled against. Report this to the API owners.'
          : error instanceof Error
            ? error.message
            : String(error),
        correlation_id: null,
        status: null,
        field_error: null,
      });
    }
  }

  /**
   * Focus moves to the outcome region **after** the phase renders.
   *
   * `aria-live` alone announces the outcome and leaves a sighted keyboard user's
   * focus on a submit button whose page has changed underneath it. Moving focus
   * to the region — and only for a settled phase, so it does not chase the
   * intermediate "hashing"/"submitting" states — puts the caret where the new
   * content is.
   */
  useEffect(() => {
    if (submitPhase.kind !== 'accepted' && submitPhase.kind !== 'refused') return;
    outcomeRef.current?.focus();
  }, [submitPhase]);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (file === null) {
      setFieldError('Choose the signed PDF to file.');
      return;
    }
    if (file.type !== '' && file.type !== 'application/pdf') {
      setFieldError('Only PDF is accepted — conversion is specified against PDF.');
      return;
    }
    if (needsTarget && targetKey === '') {
      setFieldError('Choose the contract this one relates to.');
      return;
    }
    if (sowField === 'required' && sowNumber.trim() === '') {
      setFieldError('Enter the SOW number — a SOW cannot be filed without one.');
      return;
    }

    setFieldError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void run(file, controller);
  }

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
        {/* One implementation of the description affordance, not two. */}
        <SurfaceInfo surface_id={surface.id} label={surface.label} summary={surface.summary} />
      </div>

      {options.kind === 'unavailable' ? (
        /*
         * CLM-57 Part 1. The notice classifies the failure through the shared
         * copy table, and the paragraph beneath it says the three things the
         * previous "Unavailable" stub did not: which call failed, what the
         * failure was, and that the form below is deliberately shown disabled.
         * The primary action is never removed from the page.
         */
        <>
          <ErrorNotice
            kind={options.problem}
            detail={options.problem === 'unknown' ? null : options.detail}
            correlation_id={options.correlation_id}
            action={
              <button className="btn qt" type="button" onClick={() => setAttempt((count) => count + 1)}>
                Retry
              </button>
            }
          />
          <p id="upload-unavailable" className="mut">
            {options.problem === 'not_implemented' ? (
              <>
                <strong>Not configured</strong> — guided upload is not switched on for this
                environment yet: the API has no SharePoint intake drive configured, or the write
                grant on its managed identity is still outstanding. Nothing here is broken — the
                endpoint answers honestly that the behaviour does not exist.{' '}
              </>
            ) : null}
            The call <code>GET /v1/contracts/upload-options</code> did not succeed —{' '}
            {options.problem === 'unknown'
              ? 'the API did not answer (network error). In this environment the API may not be deployed yet (B-33).'
              : options.problem === 'forbidden'
                ? 'your role may not take this action (403).'
                : options.problem === 'not_implemented'
                  ? 'the endpoint is not implemented here (501).'
                  : `the API refused it (${options.problem}).`}{' '}
            Correlation id:{' '}
            {options.correlation_id === null ? (
              'none — the request never reached the API'
            ) : (
              <code>{options.correlation_id}</code>
            )}
            . Filing is unavailable until this call succeeds; the form below is shown so you can
            see what it needs, and <strong>Retry</strong> asks again.
          </p>
        </>
      ) : null}

      <fieldset className="upload-mode" role="radiogroup" aria-label="How many documents">
        <legend className="vh">How many documents</legend>
        <label>
          <input
            type="radio"
            name="upload-mode"
            value="single"
            checked={mode === 'single'}
            disabled={busy}
            onChange={() => setMode('single')}
          />{' '}
          One document
        </label>
        <label>
          <input
            type="radio"
            name="upload-mode"
            value="bundle"
            checked={mode === 'bundle'}
            disabled={busy}
            onChange={() => setMode('bundle')}
          />{' '}
          Several (PDF or ZIP)
        </label>
      </fieldset>

      {mode === 'bundle' ? (
        <UploadBundleMode parties={parties} unavailable={options.kind !== 'ready'} />
      ) : null}

      <form className="card upload-form" onSubmit={onSubmit} aria-busy={busy} hidden={mode !== 'single'}>
        <h3>File a signed contract</h3>
        <p>
          Choose the client, the signed PDF and how the contract relates to what already exists.
          Nobody is asked for the type: it comes from the file name, and the AI reads the document.
          The contract key is minted by the API from the client — there is nothing to type, which is
          FR-14 rather than a convenience. The document goes to SharePoint through the API and stays
          there; CLM stores the reference.
        </p>

        <fieldset className="upload-kind" disabled={busy}>
          <legend>Counterparty</legend>
          {/* CLM-78 (R-76-2) — Vendor swaps the picker; the choice is never stored, the party's type is. */}
          <label>
            <input
              type="radio"
              name="upload-counterparty-kind"
              value="client"
              aria-label="Counterparty is a client"
              checked={counterpartyKind === 'client'}
              onChange={() => {
                setCounterpartyKind('client');
                setPickedParty(null);
                setPartyKey('');
                setTargetKey('');
              }}
            />{' '}
            Client agreement
          </label>
          <label>
            <input
              type="radio"
              name="upload-counterparty-kind"
              value="vendor"
              aria-label="Counterparty is a vendor"
              checked={counterpartyKind === 'vendor'}
              onChange={() => {
                setCounterpartyKind('vendor');
                setPickedParty(null);
                setPartyKey('');
                setTargetKey('');
              }}
            />{' '}
            Vendor agreement
          </label>
        </fieldset>

        <div className="frow">
          <div className="field grow">
            <label htmlFor="upload-party">{counterpartyKind === 'vendor' ? 'Vendor' : 'Client'}</label>
            {/* CLM-68 — searchable over the scoped list; the value is always a key the API offered (FR-14). */}
            {counterpartyKind === 'vendor' ? (
              /* CLM-78 — the same picker filtered to Vendor, with "Register new vendor…" (IR-03 interim). */
              <VendorPicker
                id="upload-party"
                value={partyKey}
                disabled={busy || ready === null}
                initialParties={parties}
                onChange={(party) => {
                  setPickedParty(party);
                  setPartyKey(party?.party_key ?? '');
                  setTargetKey('');
                }}
              />
            ) : (
              <PartyPicker
                id="upload-party"
                value={partyKey}
                disabled={busy || ready === null}
                initialParties={parties}
                onChange={(party) => {
                  setPickedParty(party);
                  setPartyKey(party?.party_key ?? '');
                  setTargetKey('');
                }}
              />
            )}
          </div>
        </div>

        {selectedParty?.processing_restricted === true ? (
          /*
           * §4C, before a file is chosen. A policy state, never a failure: the
           * contract is still stored, converted in-tenant, indexed and
           * searchable — only the reasoning step is withheld.
           */
          <p className="upload-restricted" role="note">
            <strong>{selectedParty.display_name} is processing-restricted.</strong> This contract
            will never be sent to an AI model, so its facts will have to be{' '}
            <strong>entered by hand</strong> after filing. It is still stored, converted inside the
            tenant, indexed and searchable.
          </p>
        ) : null}

        <div className="frow">
          <div className="field grow">
            <label htmlFor="upload-document">Signed PDF</label>
            <input
              id="upload-document"
              type="file"
              accept="application/pdf"
              disabled={busy || options.kind === 'unavailable'}
              {...(options.kind === 'unavailable' ? { 'aria-describedby': 'upload-unavailable' } : {})}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setSubmitPhase({ kind: 'idle' });
              }}
            />
            {ready !== null ? (
              <p className="mut">
                PDF only, up to {Math.floor(ready.max_document_bytes / 1_048_576)} MB.
              </p>
            ) : null}
            {/*
             * CLM-118 — the name line: what the file name gives, read by the server's own rule. With
             * amends or supersedes the type follows the related contract instead, and says so.
             */}
            {followsTarget ? (
              <p data-testid="upload-type-follows">
                Type: follows {followsLabel}.
              </p>
            ) : file === null ? null : nameType === undefined ? (
              <p className="mut" role="status" aria-busy="true" data-testid="upload-name-line">
                Reading the file name…
              </p>
            ) : nameType === null ? null : (
              <p data-testid="upload-name-line" data-evidence={nameType.evidence}>
                {nameLineWords(nameType)}
              </p>
            )}
            {!followsTarget && nameType?.amendment_signal !== null && nameType?.amendment_signal !== undefined ? (
              <p className="mut" data-testid="upload-amendment-hint">
                Looks like an amendment — if it amends a filed agreement, choose <em>amends</em>.
              </p>
            ) : null}
          </div>
        </div>

        <div className="frow">
          <div className="field">
            <label htmlFor="upload-relationship">Relationship</label>
            <select
              id="upload-relationship"
              value={relationship}
              disabled={busy}
              onChange={(event) => {
                setRelationship(event.target.value as RelationshipKind);
                setTargetKey('');
              }}
            >
              {(Object.keys(RELATIONSHIP_LABEL) as RelationshipKind[]).map((kind) => (
                <option key={kind} value={kind}>
                  {RELATIONSHIP_LABEL[kind]}
                </option>
              ))}
            </select>
          </div>

          {needsTarget ? (
            <div className="field grow">
              <label htmlFor="upload-target">Related contract</label>
              <select
                id="upload-target"
                value={targetKey}
                disabled={busy}
                onChange={(event) => setTargetKey(event.target.value)}
              >
                <option value="">Choose a contract…</option>
                {candidates.map((candidate) => (
                  <option key={candidate.contract_key} value={candidate.contract_key}>
                    {candidate.display_label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        {sowField !== 'hidden' ? (
          <div className="frow">
            <div className="field grow">
              <label htmlFor="upload-sow-number">{sowField === 'required' ? 'SOW number *' : 'SOW number'}</label>
              <input
                id="upload-sow-number"
                type="text"
                list="upload-sow-known"
                value={sowField === 'not_applicable' ? '' : sowNumber}
                disabled={busy || sowField === 'not_applicable'}
                required={sowField === 'required'}
                aria-required={sowField === 'required' ? 'true' : 'false'}
                aria-describedby="upload-sow-help"
                placeholder="e.g. AGESOW25264OV"
                onChange={(event) => setSowNumber(event.target.value)}
              />
              {/*
               * Typed, not minted (IR-01) — Qfly is not integrated, so this
               * cannot be a pure selection. FR-14 still holds: this is not the
               * contract key, which is still derived. The API runs the value
               * through the same classifier the backfill matcher uses and
               * refuses free text or a value with no digits.
               */}
              {sowField === 'not_applicable' ? (
                <p id="upload-sow-help" className="mut" data-testid="upload-sow-not-applicable">
                  {nameType?.evidence === 'conflicting'
                    ? 'Not applicable — the file name names more than one type. If this is a SOW, rename the file so its name says SOW or carries its SOW number.'
                    : `Not applicable — the file name says ${nameType?.contract_type ?? 'another type'}. If this is a SOW, rename the file to include its SOW number.`}
                </p>
              ) : (
                <p id="upload-sow-help" className="mut" data-testid="upload-sow-help">
                  {sowField === 'required'
                    ? relationship === 'supersedes'
                      ? 'Required: the contract it replaces is a SOW.'
                      : 'Required: the file name says SOW.'
                    : nameType === null || nameType === undefined
                      ? 'The file name decides: required when it says SOW, optional when it gives no type.'
                      : 'Optional — the file name gives no type; giving one files it as a SOW.'}{' '}
                  Type the number exactly as it appears on the signed document (e.g. AGESOW25264OV). CLM
                  never mints one — a SOW without it is not filed and is not extracted.
                </p>
              )}
            </div>
          </div>
        ) : null}

        <div className="frow">
          <div className="field grow">
            <label htmlFor="upload-tags">Tags (optional)</label>
            <TagInput id="upload-tags" value={tags} onChange={setTags} disabled={busy} />
            <p className="mut">
              Free-form, stored lowercase — e.g. <code>finance contracts</code>. Segment and filter by
              them on Search, Needs attention and the Pipeline. Suggestions come from tags already on
              contracts you can see.
            </p>
          </div>
        </div>

        {ready?.projected_contract_key !== null && ready?.projected_contract_key !== undefined ? (
          <p className="upload-projected">
            This will be filed as <code>{ready.projected_contract_key}</code> — one number series per
            client, whatever the type. The API re-derives the number when you submit, so if somebody
            else files for this client first you will be asked to try again.
          </p>
        ) : null}

        {fieldError !== null ? <p className="ferr">{fieldError}</p> : null}

        <button
          className="btn"
          type="submit"
          disabled={!canSubmit}
          {...(options.kind === 'unavailable' ? { 'aria-describedby': 'upload-unavailable' } : {})}
        >
          {submitPhase.kind === 'hashing'
            ? 'Reading the document…'
            : submitPhase.kind === 'submitting'
              ? 'Filing…'
              : 'File this contract'}
        </button>
      </form>

      {/*
       * Always present, always live, and focusable. Rendering it conditionally
       * would mean the region is inserted at the same moment its content
       * changes, which several screen readers do not announce at all.
       */}
      <div
        ref={outcomeRef}
        className="upload-outcome"
        role="status"
        aria-live="polite"
        tabIndex={-1}
      >
        {submitPhase.kind === 'accepted' ? (
          <>
            <p>
              <strong>Filed as {submitPhase.response.contract_key}.</strong> The document is in
              SharePoint and the contract row exists; the facts do not yet.
            </p>
            {/* CLM-110 (R-110-16), CLM-118 — the type the server resolved, with its tag, and what the file name gave. */}
            <p data-testid="upload-resolved-type">
              {/* "Type pending" names itself: no "Type" lead-in before it. */}
              {submitPhase.response.contract_type_confirmation === 'pending' ? null : 'Type '}
              <ContractTypeTag
                type={submitPhase.response.contract_type}
                confirmation={submitPhase.response.contract_type_confirmation}
              />{' '}
              — {resolvedTypeWords(submitPhase)}.
              {/* CLM-124 — an amendment's own name and reading never settle its type (B17); settling its agreement's does. */}
              {submitPhase.response.contract_type_confirmation === 'provisional' ||
              submitPhase.response.contract_type_confirmation === 'pending'
                ? submitPhase.relationship === 'amends'
                  ? ' Its type follows the agreement and is settled when the agreement’s is.'
                  : ' The AI reads the document; an administrator confirms the type unless the file name and the AI agree, or auto-approval for AI-decided types approves it.'
                : ''}
            </p>
            <p className="mut">
              {submitPhase.response.extraction_queued
                ? 'Extraction is queued and runs asynchronously — conversion, then pseudonymisation, then extraction. Check the contract record for progress.'
                : 'This contract is processing-restricted, so no extraction has been queued. Its facts must be entered by hand.'}
            </p>
            {/* CLM-112 (R-112-16) — an MSA that amends nothing is offered the deviation report, from its record.
                The type is the response's — resolved by the server (CLM-110, CLM-118). */}
            <BaseDeviationOffer
              contractKey={submitPhase.response.contract_key}
              contractType={submitPhase.response.contract_type}
              isAmendment={submitPhase.relationship === 'amends'}
            />
          </>
        ) : null}
        {submitPhase.kind === 'refused' ? (
          <>
            <p>
              <strong>Not filed.</strong>{' '}
              {/*
                * The specific answer when the gateway sent one. A `409` is three
                * different pieces of advice — the document is already filed
                * (stop), the SOW number is taken (fix the field), the sequence
                * number moved (re-open the form) — and they arrive as the same
                * uniform `conflict` message, distinguished only by
                * `field_errors[0].path`. So the field error is preferred, and
                * the generic sentence is the fallback for a body that carried
                * none.
                */}
              {submitPhase.field_error !== null
                ? submitPhase.field_error.message
                : submitPhase.status === 409
                  ? 'Something about this filing conflicts with what CLM already holds — the same document may already be filed, the SOW number may be in use, or somebody filed for this client while you were on this page. Reload and try again.'
                  : submitPhase.message}
            </p>
            {(() => {
              /* Keyed on the reason, never the path: a key_collision is not cured by reloading (R-110-10). */
              const reason = refusalReason(submitPhase.field_error);
              const advice = reason === null ? undefined : REFUSAL_ADVICE[reason];
              return advice === undefined ? null : (
                <p className="mut" data-testid="upload-refusal-advice" data-reason={reason ?? ''}>
                  {advice}
                </p>
              );
            })()}
            {submitPhase.correlation_id !== null ? (
              <p className="mut">
                Quote <code>{submitPhase.correlation_id}</code> in a support request.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
